import gsap from 'gsap';
import appStore from '../utils/state';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default class MainNavPanel extends HTMLElement {
  private isMainNavOpen: boolean = false;
  private isVisible: boolean = false;
  private panelId: string | null = null;

  private dialog: HTMLDialogElement | null = null;
  private content: HTMLElement | null = null;

  private showTl: gsap.core.Timeline | null = null;
  private hideTl: gsap.core.Timeline | null = null;

  private triggerElement: HTMLElement | null = null;

  private unsubscribeFromPanelState: (() => void) | null = null;
  private unsubscribeFromIsSearchModalOpen: (() => void) | null = null;

  private onMainNavUpdateBound = this.onMainNavUpdate.bind(this);
  private onDialogCancelBound = this.onDialogCancel.bind(this);
  private onDialogCloseBound = this.onDialogClose.bind(this);
  private onKeyDownBound = this.onKeyDown.bind(this);
  private onSearchModalUpdateBound = this.onSearchModalUpdate.bind(this);

  connectedCallback(): void {
    this.panelId = this.dataset.panelId ?? this.getAttribute('data-panel-id') ?? '';
    this.dialog = this.querySelector('dialog');
    this.content = this.querySelector('[data-main-nav-panel-ref="content"]');

    this.addEventListeners();
    this.subscribe();
  }

  addEventListeners(): void {
    if (this.dialog) {
      this.dialog.addEventListener('cancel', this.onDialogCancelBound);
      this.dialog.addEventListener('close', this.onDialogCloseBound);
      this.dialog.addEventListener('keydown', this.onKeyDownBound);
    }
  }

  removeEventListeners(): void {
    if (this.dialog) {
      this.dialog.removeEventListener('cancel', this.onDialogCancelBound);
      this.dialog.removeEventListener('close', this.onDialogCloseBound);
      this.dialog.removeEventListener('keydown', this.onKeyDownBound);
    }
  }

  subscribe(): void {
    this.unsubscribeFromPanelState = appStore.subscribeKey('activeMainNavPanelId', this.onMainNavUpdateBound);
    this.unsubscribeFromIsSearchModalOpen = appStore.subscribeKey('isSearchModalOpen', this.onSearchModalUpdateBound);
  }

  unsubscribe(): void {
    if (this.unsubscribeFromPanelState) {
      this.unsubscribeFromPanelState();
      this.unsubscribeFromPanelState = null;
    }
    if (this.unsubscribeFromIsSearchModalOpen) {
      this.unsubscribeFromIsSearchModalOpen();
      this.unsubscribeFromIsSearchModalOpen = null;
    }
  }

  onSearchModalUpdate(): void {
    if (appStore.getState().isSearchModalOpen) {
      appStore.actions.closeMainNav();
    }
  }

  onDialogCancel(event: Event): void {
    event.preventDefault();
    appStore.actions.closeMainNav();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      appStore.actions.closeMainNav();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  onDialogClose(): void {
    this.dialog?.removeAttribute('aria-modal');

    if (!appStore.getState().isMainNavOpen) {
      this.restoreFocus();
    }
  }

  private getFocusableElements(): HTMLElement[] {
    if (!this.content) {
      return [];
    }

    return Array.from(this.content.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (element) => element.offsetParent !== null || element.getClientRects().length > 0,
    );
  }

  private focusInitialContent(): void {
    const focusableElements = this.getFocusableElements();
    const firstFocusable = focusableElements[0];

    if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  private trapFocus(event: KeyboardEvent): void {
    if (!this.isVisible) {
      return;
    }

    const focusableElements = this.getFocusableElements();

    if (focusableElements.length === 0) {
      return;
    }

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (event.shiftKey) {
      if (activeElement === firstFocusable || !this.content?.contains(activeElement)) {
        event.preventDefault();
        lastFocusable.focus();
      }
      return;
    }

    if (activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
  }

  captureTrigger(): void {
    const dialogId = `main-nav-panel-dialog-${this.panelId}`;
    const trigger = document.querySelector(`[aria-controls="${dialogId}"]`);

    if (trigger instanceof HTMLElement) {
      this.triggerElement = trigger;
      return;
    }

    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && !this.contains(activeElement)) {
      this.triggerElement = activeElement;
    }
  }

  restoreFocus(): void {
    if (this.triggerElement && document.contains(this.triggerElement)) {
      this.triggerElement.focus();
    }

    this.triggerElement = null;
  }

  onMainNavUpdate(): void {
    this.isMainNavOpen = appStore.getState().isMainNavOpen;
    const activeMainNavPanelId = appStore.getState().activeMainNavPanelId;
    const previousMainNavPanelId = appStore.getState().previousMainNavPanelId;

    const wasVisible = this.panelId && previousMainNavPanelId === this.panelId;
    this.isVisible = Boolean(this.panelId && this.isMainNavOpen && activeMainNavPanelId === this.panelId);

    const quick = previousMainNavPanelId !== null;

    if (this.isVisible) {
      this.show(quick);
    } else {
      if (wasVisible && this.isMainNavOpen) {
        this.hide(true);
      }

      if (wasVisible && !this.isMainNavOpen) {
        this.hide();
      }
    }
  }

  show(quick: boolean = false): void {
    if (!this.dialog) {
      return;
    }

    if (this.hideTl) {
      this.hideTl.kill();
    }
    if (this.showTl) {
      this.showTl.kill();
    }

    if (!quick) {
      this.captureTrigger();
    }

    gsap.set(this.dialog, { autoAlpha: 0, visibility: 'visible' });

    if (!this.dialog.open) {
      this.dialog.show();
    }

    this.dialog.setAttribute('aria-modal', 'true');

    if (!quick) {
      this.focusInitialContent();
    }

    this.showTl = gsap.timeline();
    this.showTl.fromTo(this.dialog, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' }, 0);
    this.showTl.fromTo(this.dialog, { x: '-5%' }, { x: 0, force3D: true, duration: 0.7, ease: 'expo.out' }, 0);
  }

  hide(quick: boolean = false): void {
    if (!this.dialog) {
      return;
    }

    if (this.showTl) {
      this.showTl.kill();
    }
    if (this.hideTl) {
      this.hideTl.kill();
    }

    this.hideTl = gsap.timeline({
      onComplete: () => {
        if (this.dialog?.open) {
          this.dialog.close();
        }
      },
    });

    this.hideTl.to(this.dialog, { autoAlpha: 0, duration: 0.2, ease: 'power2.out' }, 0);
    this.hideTl.to(this.dialog, { x: '-5%', duration: 0.3, force3D: true, ease: 'expo.out' }, 0);
  }

  disconnectedCallback(): void {
    this.unsubscribe();
    this.removeEventListeners();

    if (this.showTl) {
      this.showTl.kill();
    }
    if (this.hideTl) {
      this.hideTl.kill();
    }
  }
}

customElements.define('main-nav-panel', MainNavPanel);
